// src/common.ts
import { z } from "zod";
if (typeof globalThis.window !== "undefined") z.config({ jitless: true });
var PROTOCOL_VERSION = 1;
var Id = z.uuid();
var B64 = z.string().regex(/^[A-Za-z0-9_-]*$/, "base64url");
var ConversationKind = z.enum(["direct", "group", "channel"]);
var MemberRole = z.enum(["owner", "admin", "member"]);
var DevicePlatform = z.enum(["web", "ios", "android", "bot"]);
var DeviceStatus = z.enum(["pending", "active", "revoked"]);
var WireKind = z.enum(["application", "commit", "proposal"]);
var ErrorCode = z.enum([
  "invalid_request",
  "unauthenticated",
  "forbidden",
  "device_pending",
  "not_found",
  "epoch_conflict",
  "duplicate",
  /** 409 External Commit: thiết bị đã ở trong nhóm / nhóm không còn thiết bị / còn thiết bị chưa hỗ trợ (client chờ thêm hộ). */
  "already_member",
  "no_devices",
  "peers_outdated",
  "too_large",
  "file_too_large",
  "rate_limited",
  /** 401: thiết bị đã bị thu hồi (DeviceList mới không còn nó) — client phải xóa khóa và dữ liệu cục bộ. */
  "device_revoked",
  /** Web Push chưa bật trên server (thiếu khóa VAPID). */
  "push_disabled",
  /** 426: app mobile (header `X-App-Version`) thấp hơn `min_supported` của nền tảng — phải cập nhật mới dùng được. */
  "upgrade_required"
]);
var ApiError = z.object({
  error: z.object({ code: ErrorCode, message: z.string() })
});

// src/payload.ts
import { z as z2 } from "zod";
var FileRef = z2.object({
  file_id: Id,
  key: B64,
  prefix: B64
});
var ENTITY_TYPES = ["bold", "italic", "underline", "strike", "code", "pre", "spoiler", "quote", "link"];
var TextEntity = z2.object({
  t: z2.enum(ENTITY_TYPES),
  o: z2.number().int().nonnegative(),
  l: z2.number().int().positive(),
  url: z2.string().max(2048).optional()
});
var MAX_ENTITIES = 200;
var TextBody = z2.object({
  text: z2.string().max(16384),
  entities: z2.array(TextEntity).max(MAX_ENTITIES).optional(),
  mentions: z2.array(Id).default([]),
  tags: z2.array(z2.string().max(64)).default([]),
  reply_to: Id.optional(),
  forwarded_from: z2.object({ conversation_id: Id, sender: Id }).optional(),
  /** W2.2: trả lời trong luồng của tin gốc này (một cấp). Client cũ bỏ trường lạ → hiển thị như tin thường. */
  thread_root: Id.optional()
});
var FileBody = FileRef.extend({
  sha256: B64,
  size: z2.number().int().nonnegative(),
  mime: z2.string(),
  name: z2.string().max(255),
  width: z2.number().int().optional(),
  height: z2.number().int().optional(),
  duration: z2.number().optional(),
  thumbnail: FileRef.optional(),
  caption: z2.string().max(4096).optional(),
  /** W2.2: file gửi trong luồng của tin gốc này. */
  thread_root: Id.optional()
});
var base = {
  v: z2.literal(1),
  id: Id,
  ts: z2.number().int(),
  topic: Id.optional(),
  expires_in: z2.number().int().positive().optional()
};
var MessagePayload = z2.discriminatedUnion("type", [
  z2.object({ ...base, type: z2.literal("text"), body: TextBody }),
  z2.object({ ...base, type: z2.literal("file"), body: FileBody }),
  z2.object({
    ...base,
    type: z2.literal("reaction"),
    body: z2.object({ target: Id, emoji: z2.string().max(32), remove: z2.boolean().optional() })
  }),
  z2.object({
    ...base,
    type: z2.literal("edit"),
    body: TextBody.pick({ text: true, entities: true, mentions: true, tags: true }).extend({ target: Id })
  }),
  z2.object({ ...base, type: z2.literal("delete"), body: z2.object({ targets: z2.array(Id).min(1) }) }),
  z2.object({
    ...base,
    type: z2.literal("receipt"),
    body: z2.object({ kind: z2.enum(["delivered", "read"]), up_to: Id })
  }),
  z2.object({
    ...base,
    type: z2.literal("group_meta"),
    body: z2.object({
      name: z2.string().max(128),
      description: z2.string().max(2048).optional(),
      avatar: FileRef.optional(),
      /**
       * Ảnh nhóm WebP nhỏ (base64url, ≤ 32 KB) nằm ngay trong tin E2EE. Thiếu = giữ nguyên; chuỗi rỗng = bỏ ảnh.
       * Người nhận kiểm lại (đúng WebP, đúng cỡ) trước khi dùng.
       */
      avatar_data: z2.string().max(44e3).optional(),
      project_id: z2.string().optional(),
      topics: z2.array(z2.object({ id: Id, name: z2.string().max(128) })).optional()
    })
  }),
  z2.object({
    ...base,
    /**
     * W2.1: ghim / bỏ ghim tin `target`. Chat 1-1: cả hai người; nhóm: chỉ owner/admin (người nhận kiểm theo vai trò
     * hiện tại như `group_meta`). Server không biết tin nào được ghim.
     */
    type: z2.literal("pin"),
    body: z2.object({ target: Id, unpin: z2.boolean().optional() })
  }),
  z2.object({
    ...base,
    /**
     * Đánh dấu / bỏ đánh dấu "quan trọng" cho tin `target`. CHỈ người gửi tin đó được đánh dấu tin của chính mình (người
     * nhận bỏ qua payload của người khác). Hiện cho mọi thành viên; server không biết tin nào quan trọng.
     */
    type: z2.literal("important"),
    body: z2.object({ target: Id, unmark: z2.boolean().optional() })
  }),
  z2.object({
    ...base,
    /**
     * W2.4: người gửi sắp tự rời nhóm — để thành viên khác ghi "đã rời" (commit gỡ thiết bị do người khác gửi nên tự nó
     * không phân biệt được rời với bị xóa). Không có nội dung.
     */
    type: z2.literal("leave"),
    body: z2.object({})
  }),
  z2.object({
    ...base,
    type: z2.literal("poll"),
    body: z2.object({
      question: z2.string().max(512),
      options: z2.array(z2.string().max(256)).min(2).max(12),
      multiple: z2.boolean()
    })
  }),
  z2.object({
    ...base,
    /**
     * Bình chọn của người gửi cho poll `target`: danh sách chỉ số lựa chọn (rỗng = rút phiếu). Phiếu mới nhất (theo
     * seq) của mỗi người thay phiếu cũ; mọi thành viên đều bỏ phiếu được.
     */
    type: z2.literal("vote"),
    body: z2.object({ target: Id, options: z2.array(z2.number().int().min(0).max(11)).max(12) })
  }),
  z2.object({
    ...base,
    /** Vị trí (WGS84). Không nhúng bản đồ: client chỉ hiện tọa độ/tên và nút mở bản đồ bên ngoài. */
    type: z2.literal("location"),
    body: z2.object({
      lat: z2.number().min(-90).max(90),
      lng: z2.number().min(-180).max(180),
      /** Độ chính xác (mét) khi lấy từ GPS. */
      accuracy: z2.number().nonnegative().optional(),
      label: z2.string().max(256).optional(),
      address: z2.string().max(512).optional()
    })
  }),
  z2.object({
    ...base,
    /**
     * Báo hiệu cuộc gọi thoại/video 1-1 (WebRTC). SDP đã gom đủ ICE candidate (không trickle) nên một cuộc gọi chỉ cần
     * `invite` (SDP offer) → `answer` (SDP answer) → `end`; `decline` khi từ chối. Đi trong kênh MLS nên máy chủ không
     * thấy SDP/dấu vân tay DTLS và người gửi được xác thực. `end` do bên gọi gửi một lần để ghi lịch sử cuộc gọi.
     */
    type: z2.literal("call"),
    body: z2.object({
      call_id: Id,
      action: z2.enum(["invite", "answer", "decline", "end"]),
      video: z2.boolean().optional(),
      sdp: z2.string().max(65536).optional(),
      outcome: z2.enum(["completed", "missed", "declined", "canceled", "busy", "failed"]).optional(),
      /** Thời lượng đàm thoại (giây), chỉ khi `outcome: completed`. */
      duration: z2.number().int().nonnegative().max(86400).optional()
    })
  })
]);
var SILENT_TYPES = /* @__PURE__ */ new Set(["receipt", "reaction", "edit", "delete", "pin", "leave", "vote", "call"]);

// src/api.ts
import { z as z3 } from "zod";
var DeviceInfo = z3.object({ name: z3.string().min(1).max(64), platform: DevicePlatform });
var PortalExchangeRequest = z3.object({
  portal_token: z3.string().min(1),
  device: DeviceInfo,
  /** Thiết bị đã đăng ký trước đó trên client này (dùng lại khóa). */
  device_id: Id.optional()
});
var PortalCodeRequest = z3.object({
  code: z3.string().min(1),
  code_verifier: z3.string().min(43).max(128),
  redirect_uri: z3.url(),
  device: DeviceInfo,
  device_id: Id.optional()
});
var AuthTokens = z3.object({
  access: z3.string(),
  refresh: z3.string(),
  /** Thời điểm access token hết hạn (epoch ms). */
  access_expires_at: z3.number().int()
});
var AuthResponse = AuthTokens.extend({
  user: z3.object({ id: Id, display_name: z3.string() }),
  device: z3.object({ id: Id, status: DeviceStatus }),
  /** Người dùng đã có danh tính (device list) chưa — quyết định luồng tạo mới / khôi phục. */
  has_identity: z3.boolean()
});
var RefreshRequest = z3.object({ refresh: z3.string().min(1) });
var UserSummary = z3.object({
  id: Id,
  display_name: z3.string(),
  kind: z3.enum(["human", "bot"]),
  has_identity: z3.boolean(),
  /** Ảnh đại diện WebP (base64url) hoặc null. */
  avatar: z3.string().nullable().default(null)
});
var DeviceSummary = z3.object({
  id: Id,
  name: z3.string(),
  platform: DevicePlatform,
  status: DeviceStatus,
  created_at: z3.iso.datetime(),
  last_seen_at: z3.iso.datetime().nullable()
});
var MeResponse = z3.object({
  user: UserSummary,
  device: DeviceSummary,
  devices: z3.array(DeviceSummary),
  /** Hồ sơ lấy từ IdP lúc đăng nhập (chỉ đọc) — hiện ở panel người dùng. */
  profile: z3.object({
    email: z3.string().nullable(),
    roles: z3.array(z3.string()),
    /** Tên từ hệ thống đăng nhập (khi người dùng đặt tên riêng thì khác `user.display_name`). */
    idp_name: z3.string().optional(),
    custom_name: z3.boolean().optional()
  }).optional()
});
var SignedDeviceList = z3.object({
  /** JSON DeviceList (crypto/identity.rs) dạng bytes. */
  payload: B64,
  signature: B64
});
var DeviceListResponse = SignedDeviceList.extend({ version: z3.number().int() }).nullable();
var KeyBackup = z3.object({ blob: B64 });
var CreateLinkRequest = z3.object({ ephemeral_pub: B64 });
var LinkRequestResponse = z3.object({
  id: Id,
  new_device_id: Id,
  ephemeral_pub: B64,
  envelope: B64.nullable(),
  expires_at: z3.iso.datetime()
});
var RespondLinkRequest = z3.object({ envelope: B64 });
var UploadKeyPackagesRequest = z3.object({
  packages: z3.array(B64).max(100),
  last_resort: B64.optional()
});
var KeyPackageCount = z3.object({ count: z3.number().int(), has_last_resort: z3.boolean() });
var ClaimKeyPackagesRequest = z3.object({ user_ids: z3.array(Id).min(1).max(100) });
var ClaimedKeyPackage = z3.object({ user_id: Id, device_id: Id, key_package: B64 });
var ClaimKeyPackagesResponse = z3.object({ packages: z3.array(ClaimedKeyPackage) });
var CreateConversationRequest = z3.object({
  kind: ConversationKind,
  mls_group_id: B64,
  project_id: z3.string().optional(),
  secret: z3.boolean().default(false),
  /** Người dùng khác (không gồm người tạo). */
  members: z3.array(Id).max(500)
});
var ConversationSummary = z3.object({
  id: Id,
  kind: ConversationKind,
  mls_group_id: B64,
  project_id: z3.string().nullable(),
  secret: z3.boolean(),
  epoch: z3.number().int(),
  last_seq: z3.number().int(),
  meta_seq: z3.number().int().nullable(),
  /** Seq của commit đã thêm thiết bị này — tin trước đó thiết bị không giải mã được. */
  joined_seq: z3.number().int(),
  /** Thành viên đang hoạt động (không gồm người đã rời/bị xóa). */
  members: z3.array(z3.object({ user_id: Id, role: MemberRole })),
  updated_at: z3.iso.datetime(),
  /** Người dùng hiện tại đã rời / bị xóa khỏi hội thoại (chỉ còn đọc lịch sử trước mốc rời). */
  left: z3.boolean().default(false),
  /** Epoch của GroupInfo server đang giữ (null = chưa có) — lệch `epoch` thì thiết bị trong nhóm đăng bản mới. */
  group_info_epoch: z3.number().int().nullable().default(null)
});
var GroupInfoBody = z3.object({ epoch: z3.number().int().nonnegative(), body: B64 });
var ExternalJoinRequest = z3.object({ epoch: z3.number().int().nonnegative(), body: B64, client_msg_id: Id });
var MlsFeaturesRequest = z3.object({ external_commit: z3.boolean() });
var InviteMembersRequest = z3.object({
  user_ids: z3.array(Id).min(1).max(100),
  /**
   * Người mời xác nhận cây MLS (trạng thái của chính họ, sau khi đã commit gỡ thiết bị cũ) không còn thiết bị nào của
   * người được mời lại. Server không đọc được cây nên chỉ dùng cờ này để đồng bộ bảng `conversation_devices` khi bảng
   * lệch (thiết bị ghi là còn trong nhóm nhưng thực tế đã ra khỏi cây) thay vì trả 400 — backlog Q1#2.
   */
  tree_synced: z3.boolean().optional()
});
var RemoveMemberRequest = z3.object({ conversation_id: Id, user_id: Id });
var UpdateRoleRequest = z3.object({ role: z3.enum(["admin", "member"]) });
var ConversationDevice = z3.object({ user_id: Id, device_id: Id });
var WelcomeOut = z3.object({ body: B64, device_ids: z3.array(Id).min(1) });
var SendMessageRequest = z3.object({
  kind: WireKind,
  /** Epoch MLS của tin; với commit là epoch TRƯỚC khi commit (server từ chối nếu lệch). */
  epoch: z3.number().int().nonnegative(),
  body: B64,
  client_msg_id: Id,
  notify: z3.boolean().default(true),
  expires_at: z3.iso.datetime().optional(),
  /** Gợi ý thay đổi thành viên cho commit (server không đọc được commit đã mã hóa). */
  added_devices: z3.array(Id).optional(),
  removed_devices: z3.array(Id).optional(),
  welcome: WelcomeOut.optional(),
  /**
   * Gợi ý: tin ứng dụng là `group_meta` (đổi tên nhóm…). Server không đọc được payload nên dựa vào cờ này để chỉ cho
   * admin/owner gửi và ghi `meta_seq`; client trung thực bỏ qua `group_meta` từ người không phải admin/owner.
   */
  meta: z3.boolean().optional(),
  /**
   * File (đã `complete`) mà tin ứng dụng này tham chiếu — server kiểm file `ready`, cùng hội thoại, do người gửi tạo,
   * rồi ghi `message_files` để xóa object khi không còn tin nào tham chiếu (C3).
   */
  file_ids: z3.array(Id).max(10).optional()
});
var SendMessageResponse = z3.object({ seq: z3.number().int() });
var WireMessage = z3.object({
  conversation_id: Id,
  seq: z3.number().int(),
  sender_user_id: Id,
  sender_device_id: Id,
  epoch: z3.number().int(),
  kind: WireKind,
  /** null nếu tin đã bị xóa. */
  body: B64.nullable(),
  created_at: z3.iso.datetime()
});
var MessagesPage = z3.object({ messages: z3.array(WireMessage), has_more: z3.boolean() });
var DeleteMessagesRequest = z3.object({ seqs: z3.array(z3.number().int()).min(1).max(100) });
var PendingWelcome = z3.object({ id: Id, conversation_id: Id, body: B64 });
var CursorUpdate = z3.array(z3.object({ conversation_id: Id, delivered_seq: z3.number().int() })).max(500);
var FILE_CHUNK_PLAIN = 1024 * 1024;
var FILE_CHUNK_TAG = 16;
var FILE_CHUNK_MAX = FILE_CHUNK_PLAIN + FILE_CHUNK_TAG;
var FILE_MAX_CHUNKS = 100;
var FILE_MAX_CIPHERTEXT = FILE_MAX_CHUNKS * FILE_CHUNK_MAX;
function fileChunkCount(ciphertextSize) {
  return Math.max(1, Math.ceil(ciphertextSize / FILE_CHUNK_MAX));
}
var CreateFileRequest = z3.object({
  conversation_id: Id,
  size: z3.number().int().min(FILE_CHUNK_TAG)
});
var CreateFileResponse = z3.object({ file_id: Id });
var CompleteFileRequest = z3.object({ sha256: B64 });
var ArchiveEntry = z3.object({ conversation_id: Id, seq: z3.number().int(), body: B64 });
var UploadArchiveRequest = z3.object({ entries: z3.array(ArchiveEntry).min(1).max(500) });
var ArchivePage = z3.object({ entries: z3.array(ArchiveEntry), has_more: z3.boolean() });
var Project = z3.object({
  id: z3.string(),
  code: z3.string(),
  name: z3.string(),
  status: z3.string()
});
var SyncProjectsRequest = z3.object({ portal_token: z3.string().min(1) });
var IceServersResponse = z3.object({
  ice_servers: z3.array(z3.object({ urls: z3.array(z3.string()), username: z3.string().optional(), credential: z3.string().optional() })),
  ttl: z3.number().int()
});
var VapidKeyResponse = z3.object({ public_key: B64 });
var PushKey = z3.string().regex(/^[A-Za-z0-9_-]+=*$/, "base64url");
var PushSubscriptionRequest = z3.object({
  endpoint: z3.url().max(2048),
  expiration_time: z3.number().nullable().optional(),
  keys: z3.object({ p256dh: PushKey.max(128), auth: PushKey.max(64) })
});
var PushPayload = z3.object({
  type: z3.literal("new_message"),
  conversation_id: Id,
  /** Tên hiển thị của người gửi (server vốn biết) — tiêu đề thông báo. Không có nội dung tin, không có tên nhóm. */
  sender_name: z3.string().optional(),
  /** URL tuyệt đối tới ảnh đại diện người gửi (ký, công khai, không cần đăng nhập); bỏ trống nếu chưa có ảnh. */
  avatar: z3.string().optional()
});
var FcmTokenRequest = z3.object({ token: z3.string().min(20).max(4096) });
var AppPlatform = z3.enum(["android", "ios"]);
var AppVersionResponse = z3.object({
  platform: AppPlatform,
  latest: z3.string(),
  min_supported: z3.string(),
  download_url: z3.string(),
  notes: z3.string()
});

// src/bots.ts
import { z as z5 } from "zod";

// src/avatar.ts
import { z as z4 } from "zod";
var AVATAR_MAX_BYTES = 32 * 1024;
var AVATAR_MAX_B64 = Math.ceil(AVATAR_MAX_BYTES * 4 / 3);
var DISPLAY_NAME_MAX = 60;
var AvatarData = B64.min(16).max(AVATAR_MAX_B64);
function b64urlBytes(s) {
  const std = s.replace(/-/g, "+").replace(/_/g, "/");
  const atobFn = globalThis.atob;
  if (!atobFn) throw new Error("M\xF4i tr\u01B0\u1EDDng kh\xF4ng c\xF3 atob");
  const bin = atobFn(std + "=".repeat((4 - std.length % 4) % 4));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}
function isAvatarWebp(b64) {
  if (b64.length < 16 || b64.length > AVATAR_MAX_B64 || !/^[A-Za-z0-9_-]+$/.test(b64)) return false;
  try {
    const bytes = b64urlBytes(b64);
    if (bytes.length > AVATAR_MAX_BYTES) return false;
    const tag = (from) => String.fromCharCode(...bytes.slice(from, from + 4));
    return tag(0) === "RIFF" && tag(8) === "WEBP";
  } catch {
    return false;
  }
}
var UpdateProfileRequest = z4.object({
  display_name: z4.string().trim().min(1).max(DISPLAY_NAME_MAX).nullable().optional(),
  avatar: AvatarData.nullable().optional()
}).refine((v) => v.display_name !== void 0 || v.avatar !== void 0, "C\u1EA7n display_name ho\u1EB7c avatar");

// src/bots.ts
var BotKind = z5.enum(["notifier", "agent"]);
var CreateBotRequest = z5.object({
  name: z5.string().trim().min(2).max(64),
  description: z5.string().trim().max(256).optional(),
  kind: BotKind.default("notifier")
});
var UpdateBotRequest = z5.object({
  name: z5.string().trim().min(2).max(64).optional(),
  description: z5.string().trim().max(256).nullable().optional(),
  /** Ảnh đại diện WebP (base64url); `null` = bỏ ảnh. */
  avatar: AvatarData.nullable().optional()
});
var BotSummary = z5.object({
  /** Id người dùng của bot (cũng là phần đầu của token). */
  id: Id,
  name: z5.string(),
  description: z5.string().nullable(),
  avatar: z5.string().nullable().default(null),
  kind: BotKind,
  owner_id: Id,
  /** Bot đã được gateway kích hoạt (có khóa/danh tính, thêm được vào nhóm). */
  has_identity: z5.boolean(),
  /** Có token đang dùng được. */
  has_token: z5.boolean(),
  created_at: z5.iso.datetime()
});
var BotTokenResponse = z5.object({ bot_id: Id, token: z5.string() });
var BotLoginRequest = z5.object({
  token: z5.string().min(40).max(200),
  device_id: Id.optional()
});

// src/announcements.ts
import { z as z6 } from "zod";
var ANNOUNCEMENT_REACTIONS = ["\u{1F44D}", "\u2764\uFE0F", "\u{1F389}", "\u{1F440}", "\u2705"];
var AnnouncementReaction = z6.enum(ANNOUNCEMENT_REACTIONS);
var AnnouncementPriority = z6.enum(["normal", "important"]);
var AnnouncementAudience = z6.discriminatedUnion("kind", [
  z6.object({ kind: z6.literal("all") }),
  z6.object({ kind: z6.literal("projects"), project_ids: z6.array(z6.string().min(1)).min(1).max(50) }),
  z6.object({ kind: z6.literal("users"), user_ids: z6.array(Id).min(1).max(500) })
]);
var ANNOUNCEMENT_IMAGE_MIMES = ["image/png", "image/jpeg", "image/gif", "image/webp"];
var ANNOUNCEMENT_ATTACHMENT_MAX_BYTES = 200 * 1024 * 1024;
var ANNOUNCEMENT_MAX_ATTACHMENTS = 10;
var ANNOUNCEMENT_CHUNK_BYTES = 1e6;
var announcementChunkCount = (size) => Math.max(1, Math.ceil(size / ANNOUNCEMENT_CHUNK_BYTES));
var AnnouncementAttachment = z6.object({
  id: Id,
  name: z6.string(),
  mime: z6.string(),
  size: z6.number().int(),
  kind: z6.enum(["image", "file"])
});
var CreateAnnouncementAttachmentRequest = z6.object({
  name: z6.string().trim().min(1).max(200),
  mime: z6.string().max(100).default("application/octet-stream"),
  size: z6.number().int().min(1).max(ANNOUNCEMENT_ATTACHMENT_MAX_BYTES)
});
var CreateAnnouncementRequest = z6.object({
  /** Kênh đăng; bỏ trống → kênh mặc định "Thông báo toàn công ty". */
  channel_id: Id.optional(),
  title: z6.string().trim().min(1).max(200),
  body: z6.string().trim().min(1).max(5e3),
  /** Tệp đã tải lên (xong) của chính người gửi, hiện dưới nội dung theo thứ tự này. */
  attachment_ids: z6.array(Id).max(ANNOUNCEMENT_MAX_ATTACHMENTS).default([]),
  priority: AnnouncementPriority.default("normal"),
  /** Người nhận phải bấm "Đã xác nhận". */
  requires_ack: z6.boolean().default(false),
  /** Trong phạm vi thành viên của kênh: `all` = mọi thành viên kênh; `projects`/`users` thu hẹp thêm. */
  audience: AnnouncementAudience,
  /** Hết hạn → ẩn khỏi danh sách "đang hiệu lực" (vẫn xem được ở lưu trữ). */
  expires_at: z6.iso.datetime().nullable().default(null)
});
var AnnouncementCounts = z6.object({ recipients: z6.number().int(), read: z6.number().int(), acked: z6.number().int() });
var Announcement = z6.object({
  id: Id,
  channel_id: Id,
  title: z6.string(),
  body: z6.string(),
  attachments: z6.array(AnnouncementAttachment).default([]),
  priority: AnnouncementPriority,
  requires_ack: z6.boolean(),
  sender: z6.object({ id: Id, display_name: z6.string() }),
  created_at: z6.iso.datetime(),
  expires_at: z6.iso.datetime().nullable(),
  /** Trạng thái của chính người xem. */
  read_at: z6.iso.datetime().nullable(),
  ack_at: z6.iso.datetime().nullable(),
  reaction: AnnouncementReaction.nullable(),
  /** Số lượt thả từng loại cảm xúc (ai cũng thấy số lượng; chỉ người gửi / quản trị thấy ai thả). */
  reaction_counts: z6.record(z6.string(), z6.number().int()).default({}),
  /** Người xem là người gửi hoặc quản trị → mở được danh sách người tương tác. */
  can_view_stats: z6.boolean().default(false),
  /** Đã đọc / xác nhận / tổng người nhận — chỉ khi `can_view_stats`. */
  counts: AnnouncementCounts.nullable().default(null)
});
var AnnouncementList = z6.object({
  items: z6.array(Announcement),
  unread: z6.number().int(),
  /** Số thông báo QUAN TRỌNG chưa đọc còn hiệu lực (ở kênh không tắt theo dõi) — dùng cho hiệu ứng lắc chuông. */
  unread_important: z6.number().int(),
  /** Người xem có quyền gửi thông báo (vai trò từ IdP). */
  can_send: z6.boolean(),
  has_more: z6.boolean()
});
var ListAnnouncementsQuery = z6.object({
  channel_id: Id.optional(),
  /** created_at của mục cuối trang trước. */
  before: z6.iso.datetime().optional(),
  limit: z6.coerce.number().int().min(1).max(100).default(30)
});
var SetReactionRequest = z6.object({ reaction: AnnouncementReaction.nullable() });
var SentAnnouncement = z6.object({
  id: Id,
  channel_id: Id,
  title: z6.string(),
  priority: AnnouncementPriority,
  requires_ack: z6.boolean(),
  created_at: z6.iso.datetime(),
  expires_at: z6.iso.datetime().nullable(),
  recipients: z6.number().int(),
  read: z6.number().int(),
  acked: z6.number().int(),
  reactions: z6.record(z6.string(), z6.number().int()).default({}),
  /** Người gửi (quản trị thấy cả thông báo của người khác). */
  sender: z6.object({ id: Id, display_name: z6.string() })
});
var AnnouncementInteraction = z6.object({
  user: z6.object({ id: Id, display_name: z6.string() }),
  read_at: z6.iso.datetime().nullable(),
  ack_at: z6.iso.datetime().nullable(),
  reaction: AnnouncementReaction.nullable(),
  reaction_at: z6.iso.datetime().nullable()
});
var AnnouncementStats = z6.object({
  id: Id,
  title: z6.string(),
  requires_ack: z6.boolean(),
  recipients: z6.number().int(),
  read: z6.number().int(),
  acked: z6.number().int(),
  /** Số lượt từng loại cảm xúc. */
  reactions: z6.record(z6.string(), z6.number().int()),
  interactions: z6.array(AnnouncementInteraction)
});
var MAX_PREFS_BYTES = 256 * 1024;
var PrefsResponse = z6.object({ version: z6.number().int(), blob: B64.nullable() });
var PutPrefsRequest = z6.object({ base_version: z6.number().int().min(0), blob: B64.max(Math.ceil(MAX_PREFS_BYTES * 4 / 3)) });

// src/channels.ts
import { z as z7 } from "zod";
var DEFAULT_CHANNEL_ID = "00000000-0000-4000-8000-0000000000c1";
var CHANNEL_GROUPS = ["official", "department", "project", "bot"];
var ChannelGroup = z7.enum(CHANNEL_GROUPS);
var CHANNEL_GROUP_LABELS = {
  official: "Ch\xEDnh th\u1EE9c",
  department: "Ph\xF2ng ban",
  project: "D\u1EF1 \xE1n",
  bot: "Bot & t\u1EF1 \u0111\u1ED9ng"
};
var CHANNEL_ICONS = ["bell", "people", "folder", "project", "shield", "calendar", "bot", "chart", "star", "hash", "task", "portal"];
var ChannelIcon = z7.enum(CHANNEL_ICONS);
var CHANNEL_COLORS = ["blue", "violet", "green", "amber", "rose"];
var ChannelColor = z7.enum(CHANNEL_COLORS);
var ChannelAudienceKind = z7.enum(["all", "projects", "users"]);
var ChannelBotRef = z7.object({ id: Id, name: z7.string() });
var Channel = z7.object({
  id: Id,
  name: z7.string(),
  description: z7.string(),
  group: ChannelGroup,
  icon: ChannelIcon,
  color: ChannelColor,
  /** Đối tượng nhận; `audience_ids` (id dự án hoặc người dùng) chỉ có với người quản trị, người khác nhận []. */
  audience_kind: ChannelAudienceKind,
  audience_ids: z7.array(z7.string()),
  member_count: z7.number().int(),
  /** Của riêng người xem. */
  unread: z7.number().int(),
  unread_important: z7.number().int(),
  /** Ghim của riêng người xem. */
  pinned: z7.boolean(),
  /** Quản trị ghim kênh cho tất cả tài khoản (hiện trên thanh ghim của mọi thành viên). */
  pinned_all: z7.boolean(),
  muted: z7.boolean(),
  /** Người xem được đăng vào kênh này / được quản trị kênh. */
  can_post: z7.boolean(),
  can_manage: z7.boolean(),
  /** Bot được phép đăng (đầy đủ với người quản trị; người khác thấy cùng danh sách để biết nguồn tự động). */
  bots: z7.array(ChannelBotRef),
  archived: z7.boolean()
});
var ChannelList = z7.object({
  channels: z7.array(Channel),
  /** Người xem có quyền tạo/gán/cài đặt kênh. */
  can_manage: z7.boolean()
});
var ChannelFields = {
  name: z7.string().trim().min(1).max(80),
  description: z7.string().trim().max(200).default(""),
  group: ChannelGroup,
  icon: ChannelIcon.default("bell"),
  color: ChannelColor.default("blue"),
  audience_kind: ChannelAudienceKind.default("all"),
  /** Id dự án (`projects`) hoặc người dùng (`users`); bỏ qua khi `all`. */
  audience_ids: z7.array(z7.string().min(1)).max(500).default([]),
  bot_ids: z7.array(Id).max(50).default([]),
  pinned_for_all: z7.boolean().default(false)
};
var audienceRule = (v) => !v.audience_kind || v.audience_kind === "all" || (v.audience_ids?.length ?? 0) > 0;
var CreateChannelRequest = z7.object(ChannelFields).refine(audienceRule, { message: "Ch\u1ECDn \xEDt nh\u1EA5t m\u1ED9t d\u1EF1 \xE1n ho\u1EB7c ng\u01B0\u1EDDi nh\u1EADn", path: ["audience_ids"] });
var UpdateChannelRequest = z7.object({
  name: ChannelFields.name.optional(),
  description: z7.string().trim().max(200).optional(),
  group: ChannelGroup.optional(),
  icon: ChannelIcon.optional(),
  color: ChannelColor.optional(),
  audience_kind: ChannelAudienceKind.optional(),
  audience_ids: z7.array(z7.string().min(1)).max(500).optional(),
  bot_ids: z7.array(Id).max(50).optional(),
  pinned_for_all: z7.boolean().optional(),
  archived: z7.boolean().optional()
}).refine(audienceRule, { message: "Ch\u1ECDn \xEDt nh\u1EA5t m\u1ED9t d\u1EF1 \xE1n ho\u1EB7c ng\u01B0\u1EDDi nh\u1EADn", path: ["audience_ids"] });
var ChannelPrefsRequest = z7.object({ pinned: z7.boolean().optional(), muted: z7.boolean().optional() });

// src/calendar.ts
import { z as z8 } from "zod";
var CALENDAR_SCOPES = ["personal", "project", "company"];
var CalendarScope = z8.enum(CALENDAR_SCOPES);
var CALENDAR_SCOPE_LABELS = { personal: "C\xE1 nh\xE2n", project: "D\u1EF1 \xE1n", company: "To\xE0n c\xF4ng ty" };
var CalendarKind = z8.enum(["event", "reminder"]);
var CALENDAR_REPEATS = ["none", "daily", "weekly", "monthly", "yearly"];
var CalendarRepeat = z8.enum(CALENDAR_REPEATS);
var CALENDAR_REPEAT_LABELS = {
  none: "Kh\xF4ng l\u1EB7p l\u1EA1i",
  daily: "H\u1EB1ng ng\xE0y",
  weekly: "H\u1EB1ng tu\u1EA7n",
  monthly: "H\u1EB1ng th\xE1ng",
  yearly: "H\u1EB1ng n\u0103m"
};
var REMIND_PRESETS = [0, 5, 10, 15, 30, 60, 120, 1440];
var MAX_REMIND_MINUTES = 10080;
var RemindMinutes = z8.array(z8.number().int().min(0).max(MAX_REMIND_MINUTES)).max(5);
var EventFields = {
  scope: CalendarScope,
  /** Bắt buộc khi `scope = project`. */
  project_id: z8.string().min(1).nullable().optional(),
  /** Nhắc hẹn nhóm chat: chỉ với `scope = personal` và `kind = reminder`; báo cho mọi thành viên nhóm. */
  conversation_id: Id.nullable().optional(),
  kind: CalendarKind.default("event"),
  title: z8.string().trim().min(1).max(200),
  description: z8.string().trim().max(2e3).default(""),
  starts_at: z8.iso.datetime(),
  ends_at: z8.iso.datetime().nullable().optional(),
  all_day: z8.boolean().default(false),
  repeat: CalendarRepeat.default("none"),
  /** Ngày kết thúc lặp (bao gồm); bỏ trống = lặp mãi. */
  repeat_until: z8.iso.datetime().nullable().optional(),
  remind_minutes: RemindMinutes.default([])
};
function checkEvent(v, ctx) {
  if (v.scope === "project" && !v.project_id) ctx.addIssue({ code: "custom", path: ["project_id"], message: "Ch\u1ECDn d\u1EF1 \xE1n" });
  if (v.scope && v.scope !== "project" && v.project_id) ctx.addIssue({ code: "custom", path: ["project_id"], message: "Ch\u1EC9 l\u1ECBch d\u1EF1 \xE1n m\u1EDBi c\xF3 d\u1EF1 \xE1n" });
  if (v.conversation_id && !(v.scope === "personal" && v.kind === "reminder"))
    ctx.addIssue({ code: "custom", path: ["conversation_id"], message: "Ch\u1EC9 nh\u1EAFc h\u1EB9n c\xE1 nh\xE2n m\u1EDBi g\u1EAFn \u0111\u01B0\u1EE3c v\u1EDBi nh\xF3m chat" });
  if (v.starts_at && v.ends_at && Date.parse(v.ends_at) < Date.parse(v.starts_at)) ctx.addIssue({ code: "custom", path: ["ends_at"], message: "K\u1EBFt th\xFAc ph\u1EA3i sau b\u1EAFt \u0111\u1EA7u" });
}
var CreateCalendarEventRequest = z8.object(EventFields).superRefine(checkEvent);
var UpdateCalendarEventRequest = z8.object({
  kind: CalendarKind.optional(),
  title: EventFields.title.optional(),
  description: z8.string().trim().max(2e3).optional(),
  starts_at: z8.iso.datetime().optional(),
  ends_at: z8.iso.datetime().nullable().optional(),
  all_day: z8.boolean().optional(),
  repeat: CalendarRepeat.optional(),
  repeat_until: z8.iso.datetime().nullable().optional(),
  remind_minutes: RemindMinutes.optional()
}).superRefine(checkEvent);
var CalendarEvent = z8.object({
  id: Id,
  scope: CalendarScope,
  project_id: z8.string().nullable(),
  conversation_id: Id.nullable(),
  kind: CalendarKind,
  title: z8.string(),
  description: z8.string(),
  starts_at: z8.iso.datetime(),
  ends_at: z8.iso.datetime().nullable(),
  all_day: z8.boolean(),
  repeat: CalendarRepeat,
  repeat_until: z8.iso.datetime().nullable(),
  remind_minutes: z8.array(z8.number().int()),
  created_by: z8.object({ id: Id, display_name: z8.string() }),
  created_at: z8.iso.datetime(),
  /** Người xem sửa/xóa được sự kiện này. */
  can_edit: z8.boolean()
});
var CalendarItem = CalendarEvent.extend({
  occurrence_start: z8.iso.datetime(),
  occurrence_end: z8.iso.datetime().nullable()
});
var CalendarListQuery = z8.object({
  from: z8.iso.datetime(),
  to: z8.iso.datetime(),
  project_id: z8.string().min(1).optional()
});
var CalendarList = z8.object({
  items: z8.array(CalendarItem),
  /** Người xem được tạo/sửa lịch toàn công ty. */
  can_manage_company: z8.boolean()
});

// src/personal-prefs.ts
import { z as z9 } from "zod";
var PREF_COLORS = ["red", "orange", "yellow", "green", "teal", "blue", "purple", "pink", "gray"];
var PrefColor = z9.enum(PREF_COLORS);
var MAX_LABELS = 50;
var MAX_GROUPS = 30;
var MAX_PINNED = 10;
var PrefLabel = z9.object({ id: z9.string().min(1).max(40), name: z9.string().trim().min(1).max(30), color: PrefColor.catch("blue") });
var PrefGroup = z9.object({ id: z9.string().min(1).max(40), name: z9.string().trim().min(1).max(40), collapsed: z9.boolean().default(false) });
var ConversationPref = z9.object({
  /** id nhãn (PrefLabel.id). */
  labels: z9.array(z9.string()).max(10).default([]),
  /** id nhóm (PrefGroup.id); thiếu = không thuộc nhóm nào. Mỗi hội thoại tối đa một nhóm. */
  group: z9.string().optional(),
  /** Thời điểm ghim (ms) — sắp xếp ghim theo thứ tự ghim; thiếu = không ghim. */
  pinned: z9.number().int().optional(),
  highlight: PrefColor.optional()
});
var PrefSettings = z9.object({
  density: z9.enum(["comfortable", "compact"]).default("comfortable"),
  /** Phát âm thanh khi có tin mới. */
  sound: z9.boolean().default(true),
  /** Hiện thông báo trình duyệt (cần quyền của trình duyệt). */
  desktopNotifications: z9.boolean().default(true),
  /** Hiện nội dung tin trong thông báo; tắt → chỉ hiện "Tin nhắn mới". */
  notificationPreview: z9.boolean().default(true),
  /** Enter để gửi (tắt → Ctrl+Enter gửi). */
  enterToSend: z9.boolean().default(true)
});
var PersonalPrefs = z9.object({
  v: z9.literal(1).default(1),
  labels: z9.array(PrefLabel).max(MAX_LABELS).default([]),
  groups: z9.array(PrefGroup).max(MAX_GROUPS).default([]),
  conversations: z9.record(z9.string(), ConversationPref).default({}),
  settings: PrefSettings.default(() => PrefSettings.parse({}))
});
var emptyPrefs = () => PersonalPrefs.parse({});
function pruneOrphans(prefs, liveConversationIds) {
  const labelIds = new Set(prefs.labels.map((l) => l.id));
  const groupIds = new Set(prefs.groups.map((g) => g.id));
  const conversations = {};
  for (const [id, c] of Object.entries(prefs.conversations)) {
    if (liveConversationIds && !liveConversationIds.has(id)) continue;
    const next = {
      ...c,
      labels: c.labels.filter((l) => labelIds.has(l)),
      ...c.group && groupIds.has(c.group) ? { group: c.group } : { group: void 0 }
    };
    if (!next.group) delete next.group;
    if (next.labels.length || next.group || next.pinned !== void 0 || next.highlight) conversations[id] = next;
  }
  return { ...prefs, conversations };
}

// src/library.ts
import { z as z10 } from "zod";
var LIBRARY_MAX_CHUNKS = 200;
var LIBRARY_DEFAULT_ROLE = "default";
var LIBRARY_META_MAX = 8192;
var LibraryFile = z10.object({
  id: Id,
  /** Kích thước ciphertext (byte) — phần tính vào hạn mức. */
  size: z10.number().int().min(0),
  meta: B64,
  meta_version: z10.number().int().min(0),
  created_at: z10.string()
});
var LibraryListResponse = z10.object({
  files: z10.array(LibraryFile),
  used_bytes: z10.number().int().min(0),
  quota_bytes: z10.number().int().min(0),
  /** Vai trò quyết định hạn mức (vai trò có hạn mức lớn nhất), hoặc `default`. */
  quota_role: z10.string(),
  /** Người này được chỉnh hạn mức của mọi vai trò (vai trò từ IdP thuộc `LIBRARY_ADMIN_ROLES`). */
  is_admin: z10.boolean()
});
var CreateLibraryFileRequest = z10.object({ size: z10.number().int().min(16) });
var CreateLibraryFileResponse = z10.object({ file_id: Id });
var CompleteLibraryFileRequest = z10.object({ sha256: B64, meta: B64.min(1).max(LIBRARY_META_MAX) });
var UpdateLibraryFileRequest = z10.object({
  base_version: z10.number().int().min(1),
  meta: B64.min(1).max(LIBRARY_META_MAX)
});
var LibraryQuotaRole = z10.object({
  role: z10.string(),
  /** Hạn mức đã đặt (byte); `null` = chưa đặt (vai trò đó dùng mức mặc định). */
  quota_bytes: z10.number().int().min(0).nullable(),
  users: z10.number().int().min(0)
});
var LibraryUsage = z10.object({
  user_id: Id,
  name: z10.string(),
  roles: z10.array(z10.string()),
  used_bytes: z10.number().int().min(0),
  quota_bytes: z10.number().int().min(0)
});
var LibraryQuotasResponse = z10.object({
  default_bytes: z10.number().int().min(0),
  roles: z10.array(LibraryQuotaRole),
  usage: z10.array(LibraryUsage)
});
var PutLibraryQuotaRequest = z10.object({
  role: z10.string().min(1).max(64),
  quota_bytes: z10.number().int().min(0).max(Number.MAX_SAFE_INTEGER).nullable()
});

// src/settings.ts
import { z as z11 } from "zod";
var OrgSettings = z11.object({
  /**
   * Bật (mặc định): người rời nhóm vẫn thấy nhóm và xem lại tin cũ. Tắt: rời / bị xóa khỏi nhóm là nhóm biến khỏi danh sách
   * của người đó và nội dung chat không còn xem được (trên mọi thiết bị).
   */
  keep_left_group_history: z11.boolean(),
  /**
   * Quét tệp trên thiết bị khi tải lên và tải xuống (client thực thi, nội dung không rời máy). `block`: chặn tệp thực thi và
   * tên giả mạo, riêng `.exe` và Office có macro chỉ cảnh báo; `warn`: mọi phát hiện chỉ cảnh báo; `off`: không quét.
   */
  file_scan_mode: z11.enum(["block", "warn", "off"]),
  /** SHA-256 (hex thường) của bản rõ các tệp cần chặn — dùng khi xử lý sự cố; chặn ở cả chế độ `warn`. */
  file_scan_blocked_hashes: z11.array(z11.string().regex(/^[0-9a-f]{64}$/)).max(1e3)
});
var ORG_SETTINGS_DEFAULTS = { keep_left_group_history: true, file_scan_mode: "block", file_scan_blocked_hashes: [] };
var OrgSettingsResponse = OrgSettings.extend({ can_manage: z11.boolean() });
var UpdateOrgSettingsRequest = OrgSettings.partial().strict();

// src/admin.ts
import { z as z12 } from "zod";
var Count = z12.number().int().min(0);
var AdminDayPoint = z12.object({ day: z12.string(), messages: Count, logins: Count });
var AdminOverview = z12.object({
  generated_at: z12.string(),
  users: z12.object({
    total: Count,
    humans: Count,
    bots: Count,
    disabled: Count,
    /** Đã có thiết bị hoạt động trong 24 giờ / 7 ngày / 30 ngày qua. */
    active_24h: Count,
    active_7d: Count,
    active_30d: Count,
    without_identity: Count
  }),
  devices: z12.object({ total: Count, web: Count, ios: Count, android: Count, bot: Count, pending: Count }),
  conversations: z12.object({ direct: Count, group: Count, channel: Count, total: Count }),
  messages: z12.object({ total: Count, last_24h: Count, last_7d: Count }),
  storage: z12.object({
    chat_files_bytes: Count,
    chat_files_count: Count,
    library_bytes: Count,
    library_count: Count,
    announcement_bytes: Count,
    announcement_count: Count,
    total_bytes: Count
  }),
  announcements: z12.object({ total: Count, last_7d: Count, channels: Count }),
  logins: z12.object({ last_24h: Count, last_7d: Count }),
  departments: Count,
  /** 14 ngày gần nhất (cũ → mới). */
  series: z12.array(AdminDayPoint)
});
var AdminDepartment = z12.object({
  id: Id,
  name: z12.string(),
  code: z12.string(),
  members: Count,
  created_at: z12.string()
});
var AdminDepartmentList = z12.object({ departments: z12.array(AdminDepartment) });
var SaveDepartmentRequest = z12.object({
  name: z12.string().trim().min(1).max(100),
  code: z12.string().trim().max(30).default("")
});
var AdminUser = z12.object({
  id: Id,
  display_name: z12.string(),
  /** Tên do IdP cung cấp (khi quản trị đã đặt tên khác). */
  idp_name: z12.string(),
  email: z12.string().nullable(),
  kind: z12.enum(["human", "bot"]),
  roles: z12.array(z12.string()),
  /** Vai trò quản trị gán thêm tại chat (tập con của `roles`). */
  extra_roles: z12.array(z12.string()),
  department_id: Id.nullable(),
  department: z12.string().nullable(),
  disabled: z12.boolean(),
  is_admin: z12.boolean(),
  created_at: z12.string(),
  last_seen_at: z12.string().nullable(),
  last_login_at: z12.string().nullable(),
  devices: Count,
  // Chỉ số sử dụng
  messages_sent: Count,
  messages_7d: Count,
  conversations: Count,
  announcements_sent: Count,
  chat_storage_bytes: Count,
  library_bytes: Count
});
var AdminUserList = z12.object({ users: z12.array(AdminUser), total: Count });
var UpdateAdminUserRequest = z12.object({
  /** Tên hiển thị do quản trị đặt; chuỗi rỗng / null = quay về tên IdP. */
  display_name: z12.string().trim().max(100).nullable(),
  extra_roles: z12.array(z12.string().trim().min(1).max(50)).max(20),
  department_id: Id.nullable()
}).partial().strict();
var AdminLoginEvent = z12.object({
  id: Id,
  at: z12.string(),
  action: z12.string(),
  user_id: Id.nullable(),
  user_name: z12.string().nullable(),
  device_id: Id.nullable(),
  platform: z12.string().nullable(),
  ip: z12.string().nullable(),
  target: z12.string().nullable()
});
var AdminLogList = z12.object({ events: z12.array(AdminLoginEvent), next_before: z12.string().nullable() });
var ADMIN_LOG_KINDS = ["login", "admin"];
var Bytes = z12.number().min(0);
var AdminSystem = z12.object({
  generated_at: z12.string(),
  node_version: z12.string(),
  platform: z12.string(),
  /** Thời gian tiến trình server đã chạy (giây). */
  uptime_s: z12.number().min(0),
  cpu: z12.object({
    cores: Count,
    /** % CPU toàn máy (mẫu ngắn), null nếu không đo được. */
    usage_pct: z12.number().min(0).max(100).nullable(),
    load_1: z12.number().min(0),
    load_5: z12.number().min(0),
    load_15: z12.number().min(0)
  }),
  memory: z12.object({
    total_bytes: Bytes,
    used_bytes: Bytes,
    available_bytes: Bytes,
    /** Giới hạn của container (cgroup) thay cho RAM máy chủ khi có. */
    limited: z12.boolean()
  }),
  process: z12.object({ rss_bytes: Bytes, heap_used_bytes: Bytes, heap_total_bytes: Bytes }),
  disks: z12.array(z12.object({ label: z12.string(), path: z12.string(), total_bytes: Bytes, free_bytes: Bytes })),
  database: z12.object({
    size_bytes: Bytes,
    connections: Count,
    max_connections: Count,
    top_tables: z12.array(z12.object({ name: z12.string(), size_bytes: Bytes }))
  }),
  storage: z12.object({ driver: z12.enum(["fs", "s3"]) }),
  realtime: z12.object({ online_devices: Count, connections: Count })
});

// src/ws.ts
import { z as z13 } from "zod";
var ClientEvent = z13.discriminatedUnion("t", [
  z13.object({ t: z13.literal("auth"), token: z13.string() }),
  z13.object({ t: z13.literal("typing"), conversation_id: Id }),
  z13.object({ t: z13.literal("ping") })
]);
var ServerEvent = z13.discriminatedUnion("t", [
  z13.object({ t: z13.literal("ready"), device_id: Id }),
  z13.object({ t: z13.literal("msg"), msg: WireMessage }),
  z13.object({ t: z13.literal("welcome"), id: Id, conversation_id: Id, body: B64 }),
  /** Hội thoại mới / đổi thành viên / đổi metadata → client tải lại danh sách. */
  z13.object({ t: z13.literal("conv"), conversation_id: Id }),
  /** Thêm/xóa/rời/đổi vai trò thành viên → client tải lại danh sách và dọn thiết bị của người đã rời khỏi nhóm MLS. */
  z13.object({ t: z13.literal("members_changed"), conversation_id: Id }),
  z13.object({ t: z13.literal("deleted"), conversation_id: Id, seqs: z13.array(z13.number().int()) }),
  /** Chủ nhóm đã xóa nhóm: client bỏ hội thoại khỏi danh sách. */
  z13.object({ t: z13.literal("conv_deleted"), conversation_id: Id }),
  z13.object({ t: z13.literal("typing"), conversation_id: Id, user_id: Id }),
  z13.object({ t: z13.literal("device_list"), user_id: Id, version: z13.number().int() }),
  /** Yêu cầu liên kết thiết bị mới (thiết bị cũ hiển thị để phê duyệt). */
  z13.object({ t: z13.literal("link_request"), id: Id }),
  /** Phiên bị thu hồi — client đăng xuất. */
  z13.object({ t: z13.literal("revoked") }),
  /** Có thông báo công ty mới (client tải lại danh sách / tăng số chưa đọc). */
  z13.object({ t: z13.literal("announcement"), id: Id }),
  /** Lịch thay đổi (tạo / sửa / xóa sự kiện hoặc nhắc hẹn): client tải lại khoảng đang xem. */
  z13.object({ t: z13.literal("calendar"), id: Id }),
  /** Đến giờ nhắc: `offset` = số phút báo trước (0 = đúng giờ). */
  z13.object({
    t: z13.literal("reminder"),
    event_id: Id,
    occurrence_start: z13.iso.datetime(),
    offset: z13.number().int(),
    kind: z13.enum(["event", "reminder"]),
    scope: z13.enum(["personal", "project", "company"]),
    title: z13.string()
  }),
  /** Tùy chọn cá nhân đổi từ thiết bị khác → client tải lại nếu `version` mới hơn bản đang có. */
  z13.object({ t: z13.literal("prefs"), version: z13.number().int() }),
  /** Tên hiển thị / ảnh đại diện của một người dùng (hoặc bot) đã đổi → client tải lại danh bạ. */
  z13.object({ t: z13.literal("profile"), user_id: Id }),
  z13.object({ t: z13.literal("pong") })
]);

// src/embed.ts
import { z as z14 } from "zod";
var envelope = { source: z14.literal("swchat"), v: z14.literal(1) };
var PortalToChat = z14.discriminatedUnion("type", [
  z14.object({ ...envelope, type: z14.literal("auth"), portal_token: z14.string().min(1) }),
  z14.object({
    ...envelope,
    type: z14.literal("navigate"),
    project_id: z14.string().optional(),
    conversation_id: Id.optional()
  })
]);
var ChatToPortal = z14.discriminatedUnion("type", [
  z14.object({ ...envelope, type: z14.literal("ready") }),
  z14.object({ ...envelope, type: z14.literal("need_auth") }),
  z14.object({ ...envelope, type: z14.literal("unread"), count: z14.number().int().nonnegative() }),
  z14.object({ ...envelope, type: z14.literal("open_external"), url: z14.url() })
]);

// src/releases.ts
import { z as z15 } from "zod";
var RELEASE_PLATFORMS = ["windows", "macos", "android"];
var ReleasePlatform = z15.enum(RELEASE_PLATFORMS);
var RELEASES_PER_PLATFORM = 10;
var Release = z15.object({
  id: Id,
  platform: ReleasePlatform,
  version: z15.string(),
  notes: z15.string(),
  file_name: z15.string(),
  /** 0 khi chưa có file (hoặc dùng URL ngoài không rõ kích thước). */
  file_size: z15.number().int().min(0),
  sha256: z15.string(),
  /** `true` = tải qua `/v1/downloads/:id/file`; `false` = chuyển tới `external_url`. */
  hosted: z15.boolean(),
  external_url: z15.string(),
  /** Bản này đã có thể tải (đã tải file lên hoặc có URL ngoài). */
  available: z15.boolean(),
  is_default: z15.boolean(),
  created_at: z15.string()
});
var ReleaseList = z15.object({ releases: z15.array(Release) });
var DownloadsResponse = z15.object({
  platforms: z15.array(z15.object({ platform: ReleasePlatform, default: Release.nullable(), recent: z15.array(Release) }))
});
var SaveReleaseRequest = z15.object({
  platform: ReleasePlatform,
  version: z15.string().trim().regex(/^[0-9A-Za-z][0-9A-Za-z.+_-]{0,31}$/, "Phi\xEAn b\u1EA3n ch\u1EC9 g\u1ED3m ch\u1EEF, s\u1ED1, d\u1EA5u . + _ -"),
  notes: z15.string().trim().max(2e3).default(""),
  /** Để trống nếu sẽ tải file lên; có giá trị → tải qua URL ngoài (https). */
  external_url: z15.string().trim().max(2e3).refine((v) => v === "" || /^https:\/\/[^\s]+$/.test(v), "URL ph\u1EA3i b\u1EAFt \u0111\u1EA7u b\u1EB1ng https://").default("")
});
var RELEASE_EXTENSIONS = {
  windows: [".exe", ".msi", ".zip"],
  macos: [".dmg", ".pkg", ".zip"],
  android: [".apk", ".zip"]
};
export {
  ADMIN_LOG_KINDS,
  ANNOUNCEMENT_ATTACHMENT_MAX_BYTES,
  ANNOUNCEMENT_CHUNK_BYTES,
  ANNOUNCEMENT_IMAGE_MIMES,
  ANNOUNCEMENT_MAX_ATTACHMENTS,
  ANNOUNCEMENT_REACTIONS,
  AVATAR_MAX_B64,
  AVATAR_MAX_BYTES,
  AdminDayPoint,
  AdminDepartment,
  AdminDepartmentList,
  AdminLogList,
  AdminLoginEvent,
  AdminOverview,
  AdminSystem,
  AdminUser,
  AdminUserList,
  Announcement,
  AnnouncementAttachment,
  AnnouncementAudience,
  AnnouncementCounts,
  AnnouncementInteraction,
  AnnouncementList,
  AnnouncementPriority,
  AnnouncementReaction,
  AnnouncementStats,
  ApiError,
  AppPlatform,
  AppVersionResponse,
  ArchiveEntry,
  ArchivePage,
  AuthResponse,
  AuthTokens,
  AvatarData,
  B64,
  BotKind,
  BotLoginRequest,
  BotSummary,
  BotTokenResponse,
  CALENDAR_REPEATS,
  CALENDAR_REPEAT_LABELS,
  CALENDAR_SCOPES,
  CALENDAR_SCOPE_LABELS,
  CHANNEL_COLORS,
  CHANNEL_GROUPS,
  CHANNEL_GROUP_LABELS,
  CHANNEL_ICONS,
  CalendarEvent,
  CalendarItem,
  CalendarKind,
  CalendarList,
  CalendarListQuery,
  CalendarRepeat,
  CalendarScope,
  Channel,
  ChannelAudienceKind,
  ChannelBotRef,
  ChannelColor,
  ChannelGroup,
  ChannelIcon,
  ChannelList,
  ChannelPrefsRequest,
  ChatToPortal,
  ClaimKeyPackagesRequest,
  ClaimKeyPackagesResponse,
  ClaimedKeyPackage,
  ClientEvent,
  CompleteFileRequest,
  CompleteLibraryFileRequest,
  ConversationDevice,
  ConversationKind,
  ConversationPref,
  ConversationSummary,
  CreateAnnouncementAttachmentRequest,
  CreateAnnouncementRequest,
  CreateBotRequest,
  CreateCalendarEventRequest,
  CreateChannelRequest,
  CreateConversationRequest,
  CreateFileRequest,
  CreateFileResponse,
  CreateLibraryFileRequest,
  CreateLibraryFileResponse,
  CreateLinkRequest,
  CursorUpdate,
  DEFAULT_CHANNEL_ID,
  DISPLAY_NAME_MAX,
  DeleteMessagesRequest,
  DeviceInfo,
  DeviceListResponse,
  DevicePlatform,
  DeviceStatus,
  DeviceSummary,
  DownloadsResponse,
  ENTITY_TYPES,
  ErrorCode,
  ExternalJoinRequest,
  FILE_CHUNK_MAX,
  FILE_CHUNK_PLAIN,
  FILE_CHUNK_TAG,
  FILE_MAX_CHUNKS,
  FILE_MAX_CIPHERTEXT,
  FcmTokenRequest,
  GroupInfoBody,
  IceServersResponse,
  Id,
  InviteMembersRequest,
  KeyBackup,
  KeyPackageCount,
  LIBRARY_DEFAULT_ROLE,
  LIBRARY_MAX_CHUNKS,
  LIBRARY_META_MAX,
  LibraryFile,
  LibraryListResponse,
  LibraryQuotaRole,
  LibraryQuotasResponse,
  LibraryUsage,
  LinkRequestResponse,
  ListAnnouncementsQuery,
  MAX_ENTITIES,
  MAX_GROUPS,
  MAX_LABELS,
  MAX_PINNED,
  MAX_PREFS_BYTES,
  MAX_REMIND_MINUTES,
  MeResponse,
  MemberRole,
  MessagePayload,
  MessagesPage,
  MlsFeaturesRequest,
  ORG_SETTINGS_DEFAULTS,
  OrgSettings,
  OrgSettingsResponse,
  PREF_COLORS,
  PROTOCOL_VERSION,
  PendingWelcome,
  PersonalPrefs,
  PortalCodeRequest,
  PortalExchangeRequest,
  PortalToChat,
  PrefColor,
  PrefGroup,
  PrefLabel,
  PrefSettings,
  PrefsResponse,
  Project,
  PushPayload,
  PushSubscriptionRequest,
  PutLibraryQuotaRequest,
  PutPrefsRequest,
  RELEASES_PER_PLATFORM,
  RELEASE_EXTENSIONS,
  RELEASE_PLATFORMS,
  REMIND_PRESETS,
  RefreshRequest,
  Release,
  ReleaseList,
  ReleasePlatform,
  RemoveMemberRequest,
  RespondLinkRequest,
  SILENT_TYPES,
  SaveDepartmentRequest,
  SaveReleaseRequest,
  SendMessageRequest,
  SendMessageResponse,
  SentAnnouncement,
  ServerEvent,
  SetReactionRequest,
  SignedDeviceList,
  SyncProjectsRequest,
  TextEntity,
  UpdateAdminUserRequest,
  UpdateBotRequest,
  UpdateCalendarEventRequest,
  UpdateChannelRequest,
  UpdateLibraryFileRequest,
  UpdateOrgSettingsRequest,
  UpdateProfileRequest,
  UpdateRoleRequest,
  UploadArchiveRequest,
  UploadKeyPackagesRequest,
  UserSummary,
  VapidKeyResponse,
  WelcomeOut,
  WireKind,
  WireMessage,
  announcementChunkCount,
  emptyPrefs,
  fileChunkCount,
  isAvatarWebp,
  pruneOrphans
};
